/**
 * Unsubscribe Page Component
 * Handles email unsubscribe requests with secure token validation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase/config';
import { Layout } from '../components/common/Layout';

interface UnsubscribeStatus {
  isUnsubscribed?: boolean;
  unsubscribedAt?: any;
  resubscribedAt?: any;
  resubscribeReason?: string;
}

export const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'initial' | 'success' | 'error'>('initial');
  const [errorMessage, setErrorMessage] = useState('');
  const [userStatus, setUserStatus] = useState<UnsubscribeStatus>({});

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const checkUnsubscribeStatus = useCallback(async () => {
    if (!email) return;

    try {
      const getUnsubscribeStatus = httpsCallable(functions, 'getUnsubscribeStatus');
      const result = await getUnsubscribeStatus({ email });
      setUserStatus(result.data as UnsubscribeStatus);
    } catch (error) {
      console.error('Error checking unsubscribe status:', error);
      // Don't show error for status check, just proceed
    }
  }, [email]);

  useEffect(() => {
    // Validate URL parameters on component mount
    if (!email || !token) {
      setStatus('error');
      setErrorMessage('Invalid unsubscribe link. Please use the link from your email.');
    } else {
      // Check current unsubscribe status
      checkUnsubscribeStatus();
    }
  }, [email, token, checkUnsubscribeStatus]);

  const handleUnsubscribe = async () => {
    if (!email || !token) return;

    setLoading(true);

    try {
      const handleUnsubscribe = httpsCallable(functions, 'handleUnsubscribe');
      const result = await handleUnsubscribe({ email, token });

      if (result.data && (result.data as any).success) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage('Failed to unsubscribe. Please try again.');
      }
    } catch (error: any) {
      console.error('Unsubscribe error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeepSubscribed = () => {
    window.location.href = '/';
  };

  return (
    <Layout showFooter={false} showHeader={false}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-3xl">
          <div className="bg-white shadow-2xl border border-neutral-100 rounded-2xl overflow-hidden">
            <div className="bg-neutral-900 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center text-lg font-semibold">
                  TU
                </div>
                <div>
                  <p className="text-sm text-neutral-200">trAIned up</p>
                  <p className="text-lg font-semibold">Email Preferences</p>
                </div>
              </div>
              <Link to="/" className="text-sm text-neutral-200 hover:text-white transition-colors">
                Back to Home
              </Link>
            </div>

            <div className="p-8 sm:p-10">
              {/* Loading State */}
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-neutral-600">Processing your request...</p>
                </div>
              )}

              {/* Initial Confirmation */}
              {status === 'initial' && !loading && (
                <div className="space-y-6">
                  <div className="space-y-2 text-center">
                    <p className="text-sm uppercase tracking-[0.2em] text-primary-600 font-semibold">Email Preferences</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">
                      Unsubscribe from Emails
                    </h2>
                    <p className="text-neutral-600 max-w-2xl mx-auto">
                      Are you sure you want to unsubscribe from all TrainedUp emails? You can resubscribe anytime by upgrading or contacting support.
                    </p>
                  </div>

                  {userStatus.isUnsubscribed ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-amber-800">Already Unsubscribed</h3>
                        <p className="text-sm text-amber-700">
                          You are already unsubscribed from our emails.
                          {userStatus.resubscribedAt && userStatus.resubscribeReason && (
                            <span className="block mt-1">
                              You were automatically resubscribed on {new Date(userStatus.resubscribedAt?.seconds * 1000).toLocaleString()} due to {userStatus.resubscribeReason}.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-primary-50 border border-primary-100 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-primary-800 mb-3">What this means</h3>
                      <ul className="grid sm:grid-cols-2 gap-3 text-sm text-primary-900/80">
                        <li className="flex items-start gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-primary-500"></span>
                          You'll stop receiving marketing and educational emails
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-primary-500"></span>
                          You'll still receive important account notifications
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-primary-500"></span>
                          New signups or upgrades will resubscribe you automatically
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-primary-500"></span>
                          Cancellation confirmations will still be sent as required
                        </li>
                      </ul>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      onClick={handleUnsubscribe}
                      className="w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-lg shadow-primary-500/20 transition-all"
                      disabled={userStatus.isUnsubscribed}
                    >
                      {userStatus.isUnsubscribed ? 'Already Unsubscribed' : 'Yes, Unsubscribe Me'}
                    </button>

                    <button
                      onClick={handleKeepSubscribed}
                      className="w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 border border-primary-100 transition-all"
                    >
                      Keep Me Subscribed
                    </button>
                  </div>
                </div>
              )}

              {/* Success State */}
              {status === 'success' && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-green-800">Successfully Unsubscribed</h3>
                      <p className="text-sm text-green-700">
                        You have been unsubscribed from all TrainedUp emails. We're sorry to see you go!
                      </p>
                    </div>
                  </div>

                  <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-6 space-y-3 text-neutral-700">
                    <p className="font-semibold text-neutral-900">Change your mind later?</p>
                    <ul className="grid sm:grid-cols-3 gap-3 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-primary-500"></span>
                        Create a new account
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-primary-500"></span>
                        Upgrade to a paid plan
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-primary-500"></span>
                        Contact our support team
                      </li>
                    </ul>
                  </div>

                  <Link
                    to="/"
                    className="inline-flex justify-center w-full sm:w-auto px-5 py-3 rounded-xl text-sm font-semibold text-primary-700 bg-white border border-primary-100 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-sm"
                  >
                    Return to TrainedUp
                  </Link>
                </div>
              )}

              {/* Error State */}
              {status === 'error' && (
                <div className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-red-800">Error</h3>
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => window.location.reload()}
                      className="w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-lg shadow-primary-500/20 transition-all"
                    >
                      Try Again
                    </button>

                    <a
                      href="mailto:support@trainedup.ai"
                      className="w-full flex justify-center py-3 px-4 rounded-xl text-sm font-semibold text-primary-700 bg-white border border-primary-100 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
                    >
                      Contact Support
                    </a>
                  </div>
                </div>
              )}

              {/* Footer Note */}
              <div className="mt-10 text-xs text-neutral-500 text-center border-t border-neutral-100 pt-4">
                <p className="mb-2">
                  <strong>Note:</strong> Even after unsubscribing, you may still receive important
                  account-related emails such as password resets, billing notifications, and
                  immediate cancellation confirmations.
                </p>
                <p>
                  If you have questions, please contact us at{' '}
                  <a href="mailto:support@trainedup.ai" className="text-primary-600 hover:text-primary-700 font-semibold">
                    support@trainedup.ai
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnsubscribePage;
